/**
 * What the panel asks the server, and how a file gets to and from the browser.
 *
 * The two byte-moving paths deliberately do not go through the JSON client.
 *
 * **A download is a plain navigation**, not `fetch` into a blob. The session
 * cookie is same-origin so the link simply works; a GET needs no CSRF token; and
 * the browser writes the bytes to disk itself, so a two-gigabyte core dump never
 * exists in a tab's memory. Building it any other way would undo the streaming
 * the server side was written for.
 *
 * **An upload sends the Blob as the request body** with `request(raw)`, so the
 * slice the browser already holds is handed to the socket rather than copied
 * into a JSON string a third larger.
 */

import { ApiError, api, request } from '@/api/client'
import type { ID } from '@/types/domain'

/** Mirrors api.HostRootResponse. */
export interface HostRoot {
  id: ID
  hostId: ID
  hostName?: string
  path: string
  label: string
  writable: boolean
  version: number
}

/** Mirrors api.HostFileEntry. */
export interface HostFileEntry {
  name: string
  dir: boolean
  symlink: boolean
  size: number
  mode: string
  modTime: string
  /** A symlink leading out of the root: shown, marked, and refused by the
   *  server. Hiding it would leave somebody wondering where a name went. */
  outside?: boolean
  /** A credential-shaped name this surface will not move. The row is still
   *  listed, because an operator has to be able to see the file is there. */
  denied?: boolean
}

/** Mirrors api.HostFilePartial — an interrupted upload, named as one. */
export interface HostFilePartial {
  name: string
  size: number
}

/** Mirrors api.HostFileListResponse. */
export interface HostFileListing {
  rootId: ID
  path: string
  writable: boolean
  entries: HostFileEntry[]
  truncated?: boolean
  partials?: HostFilePartial[]
}

export function listRoots() {
  return api.get<{ items: HostRoot[] }>('/host-roots')
}

export function listFiles(rootId: ID, path: string, signal?: AbortSignal) {
  return api.get<HostFileListing>(`/host-roots/${rootId}/files`, { path }, signal)
}

/** Mirrors api.HostTreeNode — one node of a subtree, relative to what was walked. */
export interface HostTreeNode {
  rel: string
  dir: boolean
  symlink?: boolean
  size: number
  denied?: boolean
}

/** Mirrors api.HostFileTreeResponse. */
export interface HostFileTree {
  rootId: ID
  path: string
  truncated?: boolean
  entries: HostTreeNode[]
}

/**
 * The whole shape of a folder, in one call.
 *
 * Recursing through `listFiles` was the obvious alternative and is wrong: a
 * folder of two hundred files under thirty directories would be two hundred and
 * thirty SSH round trips before the first byte moved, and any of them can fail
 * halfway leaving a partial tree with no account of it.
 *
 * `truncated` on the answer is not advisory. A mirror of a truncated tree is a
 * folder that looks complete and is not, so a caller must refuse it rather than
 * copy the prefix.
 */
export function walkTree(rootId: ID, path: string, signal?: AbortSignal) {
  return api.get<HostFileTree>(`/host-roots/${rootId}/tree`, { path }, signal)
}

/**
 * Makes one directory on the host, and says so when the name is taken.
 *
 * One, never a chain — the server refuses `mkdir -p` deliberately. Mirroring
 * does not need it: the tree is walked top-down, so a parent always exists by
 * the time its child is asked for.
 *
 * This is the call an *operator* makes. It is separate from [makeDir] because
 * the two want opposite things from one 409: a mirror asking for a directory
 * that is already there has got what it wanted, and a person who typed a name
 * and pressed a button has not. Routing the dialog through [makeDir] reported
 * success over an existing entry — including over a *file* of that name, which
 * `mkdirScript` also refuses with exit 9 — so the operator was told they had
 * made a folder that was somebody else's, with somebody else's contents in it.
 */
export function createDir(rootId: ID, path: string, signal?: AbortSignal): Promise<void> {
  return request<void>(`/host-roots/${rootId}/dir`, { method: 'POST', body: { path }, signal })
}

/**
 * Makes one directory for a *mirror*, where "already there" is success.
 *
 * That split is the right one: the server states what it found and the caller
 * decides what that means, rather than the server assuming idempotence on
 * everyone's behalf. See [createDir] for the caller that must not.
 */
export async function makeDir(rootId: ID, path: string, signal?: AbortSignal): Promise<void> {
  try {
    await createDir(rootId, path, signal)
  } catch (err) {
    // `exists`, and only `exists`. Four refusals on this surface share 409 —
    // a taken name, a directory that will not empty, a read-only root, and a
    // root missing from its host — and swallowing the status rather than the
    // reason meant a mirror into a root somebody had just marked read-only
    // reported every directory as created and then failed on the first byte.
    if (reasonOf(err) === 'exists') return
    throw err
  }
}

/**
 * Which refusal this is, when the status alone cannot say.
 *
 * The server sends a word and an English sentence; the word is what travels and
 * the sentence is a fallback for anything reading this API without a
 * translation table. Composing the reader's sentence here is the rule ADR-0026
 * §10 set for this module after an operator met English prose in a Turkish
 * console — this is the same rule applied to the refusals ADR-0027 adds.
 */
export type HostFileReason =
  | 'outsideRoot'
  | 'deniedName'
  | 'noRoot'
  | 'exists'
  | 'notEmpty'
  | 'readOnly'
  | 'offsetMismatch'
  | 'notAFile'
  | 'checksum'

export function reasonOf(err: unknown): HostFileReason | undefined {
  if (!(err instanceof ApiError)) return undefined
  return err.fields?.reason as HostFileReason | undefined
}

/**
 * The URL a browser downloads from.
 *
 * Handed to an anchor rather than fetched. `encodeURIComponent` on the path
 * because a filename can hold a space, a `#` or a `&`, and any of those unescaped
 * turns into a different request — one for a file that does not exist, or worse,
 * for a different one.
 */
export function downloadUrl(rootId: ID, path: string): string {
  return `/api/v1/host-roots/${rootId}/download?path=${encodeURIComponent(path)}`
}

/**
 * Saves one file by clicking a hidden link.
 *
 * `download` on the anchor is what stops a browser from navigating away when the
 * response happens to be something it can render — a `.log` or a `.json` would
 * otherwise open in the tab and the operator would lose the screen they were on.
 */
export function saveOne(rootId: ID, path: string, name: string): void {
  const a = document.createElement('a')
  a.href = downloadUrl(rootId, path)
  a.download = name
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * Saves several, spaced out.
 *
 * A browser that receives a burst of programmatic downloads either prompts once
 * for the lot or silently drops all but the first, and which it does depends on
 * the browser. Spacing them is what makes the common case — three or four files
 * — behave, and it is deliberately *not* a server-side archive: a tar built on
 * the host would put a member list outside the per-file confinement check, which
 * is the one property this surface is built around.
 *
 * The gap is a compromise rather than a measured number, and this is the first
 * place to look if a multi-select of ten behaves oddly.
 */
export const MULTI_SAVE_GAP_MS = 300

export function saveMany(rootId: ID, files: Array<{ path: string; name: string }>): void {
  files.forEach((f, i) => {
    if (i === 0) {
      saveOne(rootId, f.path, f.name)
      return
    }
    window.setTimeout(() => saveOne(rootId, f.path, f.name), i * MULTI_SAVE_GAP_MS)
  })
}

/** How far the host got with a partial upload. Zero for a fresh one, which is
 *  what makes "start" and "resume" the same call.
 *
 *  Cancellable, because it brackets the chunk loop: a stop pressed during the
 *  probe or during a forty-second `sha256sum` at the far end has to do
 *  something, and `api.post` carries no signal. */
export function probeUpload(rootId: ID, path: string, signal?: AbortSignal) {
  return request<{ path: string; offset: number }>(`/host-roots/${rootId}/upload/probe`, {
    method: 'POST',
    body: { path },
    signal,
  })
}

/**
 * Sends one part.
 *
 * A 409 means the host is at a different offset and carries the true one in
 * `fields.offset`. It is returned rather than thrown, because it is not a
 * failure: it is the host correcting the client, and the client's answer is to
 * believe it.
 */
export async function appendChunk(
  rootId: ID,
  path: string,
  offset: number,
  blob: Blob,
  signal?: AbortSignal,
): Promise<{ ok: true } | { ok: false; have: number }> {
  try {
    await request<void>(`/host-roots/${rootId}/upload`, {
      method: 'PUT',
      raw: blob,
      query: { path, offset: String(offset) },
      signal,
    })
    return { ok: true }
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const have = Number(err.fields?.offset)
      if (Number.isFinite(have)) return { ok: false, have }
    }
    throw err
  }
}

/**
 * Verifies the accumulated bytes and puts them in place.
 *
 * `overwrite` defaults to absent, and absent means refuse: the host answers 409
 * when a regular file is already at that name, and the console asks. Until
 * ADR-0027 §6 this call replaced it without a word — the same act `hostfs.Move`
 * has always refused on a rename, for the reason written there.
 *
 * A refusal leaves the partial file on the host. That is deliberate and is what
 * makes the question askable at all: the answer may be "yes, replace", and
 * re-sending four gigabytes because somebody was asked is not an answer.
 */
export function finishUpload(
  rootId: ID,
  path: string,
  sha256: string,
  opts: { mode?: string; overwrite?: boolean; signal?: AbortSignal } = {},
) {
  return request<void>(`/host-roots/${rootId}/upload/finish`, {
    method: 'POST',
    body: { path, sha256, mode: opts.mode, overwrite: opts.overwrite },
    signal: opts.signal,
  })
}

export function abortUpload(rootId: ID, path: string) {
  return api.post<void>(`/host-roots/${rootId}/upload/abort`, { path })
}

/**
 * Removes one entry: a file, an empty directory, or a symlink (unlinked as
 * itself, never followed).
 *
 * One entry per call, and a directory goes through `rmdir`, so the kernel is
 * what refuses a non-empty one. A recursive removal is the console walking the
 * tree and calling this per node — ADR-0027 §2 records what that does and does
 * not preserve, in the words it deserves rather than the flattering ones.
 *
 * Unlike {@link makeDir}, a 409 here is not swallowed. "Already there" is
 * success when mirroring; "not empty" is a refusal the operator has to see.
 */
export function removeEntry(rootId: ID, path: string) {
  return api.del<void>(`/host-roots/${rootId}/entry?path=${encodeURIComponent(path)}`)
}

/**
 * Renames or moves one entry inside the root. Never overwrites — a name that is
 * taken comes back 409, and the dialog says so and keeps what was typed.
 */
export function moveEntry(rootId: ID, from: string, to: string) {
  return api.post<void>(`/host-roots/${rootId}/move`, { from, to })
}

export function createRoot(body: { hostId: ID; path: string; label: string; writable: boolean }) {
  return api.post<HostRoot>('/host-roots', body)
}

export function updateRoot(id: ID, body: { label?: string; writable?: boolean; version: number }) {
  return api.patch<HostRoot>(`/host-roots/${id}`, body)
}

export function removeRoot(id: ID) {
  return api.del<void>(`/host-roots/${id}`)
}
